import { Badge, Box, Text } from "@mantine/core";
import {
  MantineReactTable,
  MRT_ColumnDef,
  useMantineReactTable,
} from "mantine-react-table";
import { _localization } from "../../config/location";
import { useEffect, useState } from "react";
import React from "react";
import { repositoryWeb } from "../../_base/_const/_constVar";
import { MessageResponse } from "../../model/MessageResponse";

const ProductCategory = ({ categoryId }: { categoryId: number }) => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<any[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "itemCode",
        header: "Mã sản phẩm",
        Cell: ({ renderedCellValue }) => (
          <Badge variant="dot" color="green">
            {renderedCellValue}
          </Badge>
        ),
      },
      { accessorKey: "itemName", header: "Tên sản phẩm" },
      {
        accessorKey: "brandName",
        header: "Thương hiệu",
        Cell: ({ renderedCellValue }) => (
          <Badge variant="dot" color="blue">
            {renderedCellValue}
          </Badge>
        ),
      },
      { accessorKey: "orderNumber", header: "Thứ tự hiển thị" },
      {
        accessorKey: "unitSellingPrice",
        header: "Giá bán",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue || Number(renderedCellValue) === 0 ? (
            <Text fw={"bold"} size="14px">
              {Number(renderedCellValue).toLocaleString()} VNĐ
            </Text>
          ) : (
            <></>
          ),
      },
      {
        accessorKey: "marketPrice",
        header: "Giá thị trường",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue || Number(renderedCellValue) === 0 ? (
            <Text fw={"bold"} size="14px">
              {Number(renderedCellValue).toLocaleString()} VNĐ
            </Text>
          ) : (
            <></>
          ),
      },
      {
        accessorKey: "inUse",
        header: "Đang sử dụng",
        Cell: ({ renderedCellValue }) => (
          <Badge color={renderedCellValue === "Y" ? "green" : "red"}>
            {renderedCellValue === "Y" ? "Hiển thị" : "Không hiển thị"}
          </Badge>
        ),
      },
    ],
    []
  );

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);
    try {
      let url = `/TblItem/get-items-by-category?CategoryId=${categoryId}&Skip=${
        pagination.pageIndex * pagination.pageSize
      }&Take=${pagination.pageSize}`;

      const res = await repositoryWeb.get<MessageResponse<any>>(url);

      if (res && res.success) {
        const result = res.data;
        setData(result);
        setRowCount(res.totalCount);
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination?.pageIndex, pagination?.pageSize]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (150 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: false,
    positionToolbarAlertBanner: "bottom",
    enableRowNumbers: true,
    //add custom action buttons to top-left of top toolbar
    enableTopToolbar: false,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    getRowId: (row) => row.id?.toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        left: [],
        right: [],
      },
      columnVisibility: { id: true, phone: false, fax: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    manualPagination: true,
    rowCount: rowCount,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        fontWeight: "500",
        fontSize: "12.5px",
        padding: "5px 15px",
      },
    }),
    state: {
      isLoading,
      pagination: pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["25", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    columnFilterDisplayMode: "popover",
    mantineTableBodyRowProps: ({ row }) => ({
      style: { cursor: "pointer" },
    }),
  });

  return (
    <>
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </>
  );
};

export default ProductCategory;
